import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface ClientBranding {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  companyName: string;
  customDomain: string | null;
}

interface ClientFeatures {
  csvUpload: boolean;
  bulkProcessing: boolean;
  statusTracking: boolean;
  reportGeneration: boolean;
  apiAccess: boolean;
  customFields: boolean;
}

interface CustomRoute {
  path: string;
  component: string;
  title: string;
  icon: string;
  requiresAuth: boolean;
}

interface ClientConfiguration {
  success: boolean;
  companyId: string | null;
  companyName: string;
  portalType: 'didi' | 'standard' | 'enterprise' | 'custom';
  branding: ClientBranding;
  features: ClientFeatures;
  dashboardLayout: 'standard' | 'compact' | 'detailed' | 'custom';
  customRoutes: CustomRoute[];
  assignmentManager: any;
  settings: any;
}

interface ClientConfigContextType {
  config: ClientConfiguration | null;
  loading: boolean;
  error: string | null;
  loadConfiguration: (email: string, companyName?: string) => Promise<void>;
  getPortalRoute: (email: string, companyName?: string) => Promise<string>;
  validateAccess: (email: string, portalType: string, companyId?: string) => Promise<boolean>;
  updateTheme: () => void;
  isFeatureEnabled: (feature: keyof ClientFeatures) => boolean;
  getCustomRoutes: () => CustomRoute[];
}

const ClientConfigContext = createContext<ClientConfigContextType | undefined>(undefined);

interface ClientConfigProviderProps {
  children: ReactNode;
}

export const ClientConfigProvider: React.FC<ClientConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<ClientConfiguration | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfiguration = async (email: string, companyName?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/client-config/get', {
        email,
        companyName
      });
      
      if (response.data.success) {
        setConfig(response.data.data);
        updateTheme(response.data.data.branding);
      } else {
        setError('Failed to load client configuration');
      }
    } catch (err: any) {
      console.error('Error loading client configuration:', err);
      setError(err.response?.data?.message || 'Failed to load configuration');
      
      // Set default configuration on error
      setConfig(getDefaultConfiguration());
    } finally {
      setLoading(false);
    }
  };

  const getPortalRoute = async (email: string, companyName?: string): Promise<string> => {
    try {
      const response = await axios.post('/api/client-config/portal-route', {
        email,
        companyName
      });
      
      if (response.data.success) {
        return response.data.data.route;
      }
    } catch (err) {
      console.error('Error getting portal route:', err);
    }
    
    return '/client-portal'; // Default fallback
  };

  const validateAccess = async (email: string, portalType: string, companyId?: string): Promise<boolean> => {
    try {
      const response = await axios.post('/api/client-config/validate-access', {
        email,
        portalType,
        companyId
      });
      
      return response.data.success && response.data.data.hasAccess;
    } catch (err) {
      console.error('Error validating access:', err);
      return false;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateTheme = (branding?: ClientBranding) => {
    const brandingToUse = branding || config?.branding;
    if (!brandingToUse) return;

    // Update CSS custom properties for theming
    const root = document.documentElement;
    root.style.setProperty('--primary-color', brandingToUse.primaryColor);
    root.style.setProperty('--secondary-color', brandingToUse.secondaryColor);
    
    // Update document title
    if (brandingToUse.companyName) {
      document.title = `${brandingToUse.companyName} - Professional Background Verification`;
    }
  };

  const isFeatureEnabled = (feature: keyof ClientFeatures): boolean => {
    return config?.features[feature] ?? false;
  };

  const getCustomRoutes = (): CustomRoute[] => {
    return config?.customRoutes || [];
  };

  const getDefaultConfiguration = (): ClientConfiguration => {
    return {
      success: true,
      companyId: null,
      companyName: 'Valida.do Client',
      portalType: 'standard',
      branding: {
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        logoUrl: null,
        companyName: 'Valida.do',
        customDomain: null
      },
      features: {
        csvUpload: true,
        bulkProcessing: false,
        statusTracking: true,
        reportGeneration: false,
        apiAccess: false,
        customFields: false
      },
      dashboardLayout: 'standard',
      customRoutes: [],
      assignmentManager: null,
      settings: {
        allowBulkUploads: false,
        maxChecksPerMonth: 100,
        autoApproveChecks: false
      }
    };
  };

  // Load configuration from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('clientConfiguration');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(parsedConfig);
        updateTheme(parsedConfig.branding);
      } catch (err) {
        console.error('Error parsing saved configuration:', err);
      }
    }
  }, [updateTheme]);

  // Save configuration to localStorage when it changes
  useEffect(() => {
    if (config) {
      localStorage.setItem('clientConfiguration', JSON.stringify(config));
    }
  }, [config]);

  const value: ClientConfigContextType = {
    config,
    loading,
    error,
    loadConfiguration,
    getPortalRoute,
    validateAccess,
    updateTheme,
    isFeatureEnabled,
    getCustomRoutes
  };

  return (
    <ClientConfigContext.Provider value={value}>
      {children}
    </ClientConfigContext.Provider>
  );
};

export const useClientConfig = (): ClientConfigContextType => {
  const context = useContext(ClientConfigContext);
  if (context === undefined) {
    throw new Error('useClientConfig must be used within a ClientConfigProvider');
  }
  return context;
};

export default ClientConfigContext;